import { match } from 'path-to-regexp'

import { generateSentence, getRandomItem } from './randomizer'
import { ApiError } from './api-error'
import { mockEndpoints } from '../fake-db'

// Default mock configuration
const DEFAULT_MOCK_CONFIG = {
  enabled: true, // Enable/disable mocking
  delay: 0, // Delay in milliseconds. 0 = no delay, 1000 = 1 second
  errorRate: 0, // Random error rate (0-1). 0 = no errors, 1 = all errors, 0.5 = 50% errors
  headers: {}, // Custom response headers. e.g. { 'x-mock': 'enabled' }
  error: null, // { status: 500, message: 'Error message', details: [] }
}

/**
 * Normalize mock configuration
 * @param {boolean | object} config - Mock configuration
 * @returns {object} Normalized config
 */
function normalizeMockConfig(config) {
  if (typeof config === 'boolean') {
    return { ...DEFAULT_MOCK_CONFIG, enabled: config }
  }

  return {
    ...DEFAULT_MOCK_CONFIG,
    ...config,
    headers: {
      ...DEFAULT_MOCK_CONFIG.headers,
      ...(config?.headers || {}),
    },
  }
}

/**
 * Generate mock error response
 * @param {object} options
 * @param {string} options.message - Error message
 * @param {number} options.status - HTTP status code
 * @param {object[]} options.details - Error details
 */
function createErrorResponse({ message, status, details }) {
  return {
    data: {
      success: false,
      errorMessage: message,
      errors: details,
      statusCode: status,
    },
    status,
    statusText: 'Error',
    headers: {},
  }
}

/**
 * Handle error scenarios based on configuration
 * @param {object} config
 */
function handleErrorScenarios(config) {
  // Forced error scenario
  if (config.error) {
    const { status = 500, message = 'Mock Error', details = [] } = config.error
    throw new ApiError(message, {
      statusCode: status,
      errors: details,
    })
  }

  // Random error generation
  if (Math.random() < config.errorRate) {
    const errors = [
      { status: 400, message: 'Bad Request' },
      { status: 401, message: 'Unauthorized' },
      { status: 404, message: 'Not Found' },
      { status: 500, message: 'Internal Server Error' },
    ]

    const error = getRandomItem(errors)
    throw new ApiError(
      `Random mock error: ${error.message}`,
      error.status,
      [{ code: 'MOCK_ERROR', detail: generateSentence() }],
    )
  }
}

/**
 * Validates and processes the matched endpoint
 * @param {string} endpointKey - Matched endpoint key (e.g., 'GET /apps')
 * @returns {object} Method and path components
 * @throws {ApiError} When endpoint key is invalid
 */
function processEndpointKey(endpointKey) {
  if (!endpointKey || typeof endpointKey !== 'string') {
    throw new ApiError('Invalid endpoint key format', 500)
  }

  const parts = endpointKey.split(' ')
  if (parts.length !== 2 || !['GET', 'POST', 'PUT', 'DELETE', 'PATCH'].includes(parts[0])) {
    throw new ApiError(`Invalid endpoint format: ${endpointKey}`, 500)
  }

  return {
    method: parts[0],
    path: parts[1],
  }
}

/**
 * Extracts parameters from the request path
 * @param {string} endpointPath - Defined endpoint path (e.g., '/apps/:trackId')
 * @param {string} requestPath - Actual request path
 * @returns {object} Extracted parameters
 */
function extractPathParams(endpointPath, requestPath) {
  try {
    const matcher = match(endpointPath, { decode: decodeURIComponent })
    const result = matcher(requestPath)
    return result ? result.params : {}
  }
  catch (error) {
    throw new ApiError(`Path parameter extraction failed: ${error.message}`, 500)
  }
}

export async function mockAdapter(config) {
  try {
    const mockConfig = normalizeMockConfig(config.mock)

    // Skip if mocking is disabled
    if (!mockConfig.enabled) {
      throw new ApiError('Mocking is disabled', 500)
    }

    // Handle error scenarios
    handleErrorScenarios(mockConfig)

    // Extract request details
    const method = config.method.toUpperCase()
    const path = config.url.replace(config.baseURL, '')

    // Find matching endpoint
    const endpointKey = Object.keys(mockEndpoints).find((key) => {
      const { method: endpointMethod, path: endpointPath } = processEndpointKey(key)
      const matcher = match(endpointPath, { decode: decodeURIComponent })
      return endpointMethod === method && matcher(path)
    })

    if (!endpointKey) {
      throw new ApiError(`Mock endpoint not found: ${method} ${path}`, 404)
    }

    // Process endpoint key
    const { path: endpointPath } = processEndpointKey(endpointKey)

    // Extract parameters
    const params = extractPathParams(endpointPath, path)

    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, mockConfig.delay))

    // Generate mock response
    const handler = mockEndpoints[endpointKey]
    if (typeof handler !== 'function') {
      throw new ApiError(`Invalid mock handler for endpoint: ${endpointKey}`, 500)
    }

    const responseData = await handler(params, config)

    return {
      data: responseData,
      status: 200,
      statusText: 'OK',
      headers: mockConfig.headers,
      config,
    }
  }
  catch (error) {
    if (error instanceof ApiError) {
      return createErrorResponse({
        status: error.statusCode,
        message: error.message,
        details: error.errors,
      })
    }

    return createErrorResponse({
      status: 500,
      message: 'Mock processing failed',
      details: [{ message: error.message }],
    })
  }
}
